/**
 * Traffic Source Tracking
 *
 * Captures HOW users found VidSeeds.ai (search engine, social network, ad, referral, direct)
 * and persists it in a cookie so the data survives the OAuth redirect flow.
 *
 * Client-side: TrafficSourceTracker component captures on first visit.
 * Server-side: readTrafficSourceCookie() / parseTrafficSourceFromCookieHeader() reads it during signup.
 */

import { reportServerError } from "@/lib/observability/report-server-error";

export const TRAFFIC_SOURCE_COOKIE = "vs_traffic_source";

/** Max-age for the cookie: 90 days (standard attribution window). */
export const TRAFFIC_SOURCE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

export interface TrafficSource {
  /** Source name: "google", "facebook", "newsletter", "techblog.com", "direct" */
  source: string;
  /** Medium: "organic", "cpc", "social", "email", "referral", "none" */
  medium: string;
  /** UTM campaign name */
  campaign?: string;
  /** UTM term */
  term?: string;
  /** UTM content */
  content?: string;
  /** Hostname of the referring page */
  referringDomain?: string;
  /** Full URL of the referring page */
  referringUrl?: string;
  /** Landing page pathname + search (e.g., "/pricing?ref=VS123ABC") */
  landingPage: string;
  /** Search query if detectable from referrer URL */
  searchQuery?: string;
  /** ISO timestamp of first visit */
  timestamp: string;
}

// ---------------------------------------------------------------------------
// Domain classification tables
// ---------------------------------------------------------------------------

const SEARCH_ENGINES: Array<{
  keyword: string;
  name: string;
  queryParam: string;
}> = [
  { keyword: "google", name: "google", queryParam: "q" },
  { keyword: "bing.com", name: "bing", queryParam: "q" },
  { keyword: "yahoo", name: "yahoo", queryParam: "p" },
  { keyword: "duckduckgo", name: "duckduckgo", queryParam: "q" },
  { keyword: "baidu", name: "baidu", queryParam: "wd" },
  { keyword: "yandex", name: "yandex", queryParam: "text" },
  { keyword: "ecosia", name: "ecosia", queryParam: "q" },
  { keyword: "ask.com", name: "ask", queryParam: "q" },
  { keyword: "naver.com", name: "naver", queryParam: "query" },
  { keyword: "sogou.com", name: "sogou", queryParam: "query" },
];

/** Maps domain suffixes to canonical social network names. */
const SOCIAL_DOMAINS: Array<[string, string]> = [
  ["facebook.com", "facebook"],
  ["fb.com", "facebook"],
  ["fb.me", "facebook"],
  ["twitter.com", "twitter"],
  ["x.com", "twitter"],
  ["t.co", "twitter"],
  ["linkedin.com", "linkedin"],
  ["lnkd.in", "linkedin"],
  ["instagram.com", "instagram"],
  ["tiktok.com", "tiktok"],
  ["youtube.com", "youtube"],
  ["youtu.be", "youtube"],
  ["reddit.com", "reddit"],
  ["pinterest.com", "pinterest"],
  ["pin.it", "pinterest"],
  ["snapchat.com", "snapchat"],
  ["threads.net", "threads"],
  ["bsky.app", "bluesky"],
  ["mastodon.social", "mastodon"],
  ["whatsapp.com", "whatsapp"],
  ["telegram.org", "telegram"],
  ["t.me", "telegram"],
  ["discord.com", "discord"],
  ["discord.gg", "discord"],
];

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Check if a hostname matches a domain (including subdomains).
 * e.g., "m.facebook.com" matches "facebook.com", "facebook.com" matches "facebook.com".
 */
function domainEndsWith(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith("." + domain);
}

function tryParseUrl(raw: string): URL | null {
  try {
    return new URL(raw);
  } catch (error) {
    console.error("[traffic-source] Failed to parse URL:", raw, error);
    reportServerError(error, {
      source: "lib.analytics.traffic-source",
      feature: "analytics",
      action: "tryParseUrl",
      handled: true,
      severity: "warning",
    });
    return null;
  }
}

/**
 * Parse the traffic source from the referring URL and the landing page URL.
 * Pure function — used both client-side and server-side.
 */
export function parseTrafficSource(
  referrer: string,
  currentUrl: string
): TrafficSource {
  const url = tryParseUrl(currentUrl);
  const landingPage = url ? url.pathname + url.search : currentUrl || "/";
  const now = new Date().toISOString();

  if (!url) {
    return { source: "direct", medium: "none", landingPage, timestamp: now };
  }

  // ----- 1. UTM parameters (highest priority) -----
  const utmSource = url.searchParams.get("utm_source");
  if (utmSource) {
    return {
      source: utmSource,
      medium: url.searchParams.get("utm_medium") || "unknown",
      campaign: url.searchParams.get("utm_campaign") || undefined,
      term: url.searchParams.get("utm_term") || undefined,
      content: url.searchParams.get("utm_content") || undefined,
      referringDomain: referrer ? tryParseUrl(referrer)?.hostname : undefined,
      referringUrl: referrer || undefined,
      landingPage,
      timestamp: now,
    };
  }

  // ----- 2. Paid click IDs -----
  if (url.searchParams.get("gclid")) {
    return {
      source: "google",
      medium: "cpc",
      referringDomain: referrer ? tryParseUrl(referrer)?.hostname : undefined,
      referringUrl: referrer || undefined,
      landingPage,
      timestamp: now,
    };
  }
  if (url.searchParams.get("fbclid")) {
    return {
      source: "facebook",
      medium: "cpc",
      referringDomain: referrer ? tryParseUrl(referrer)?.hostname : undefined,
      referringUrl: referrer || undefined,
      landingPage,
      timestamp: now,
    };
  }
  if (url.searchParams.get("msclkid")) {
    return {
      source: "bing",
      medium: "cpc",
      referringDomain: referrer ? tryParseUrl(referrer)?.hostname : undefined,
      referringUrl: referrer || undefined,
      landingPage,
      timestamp: now,
    };
  }

  // ----- 3. Referrer-based classification -----
  if (referrer) {
    const refUrl = tryParseUrl(referrer);
    if (!refUrl) {
      return {
        source: referrer,
        medium: "referral",
        landingPage,
        timestamp: now,
      };
    }

    const refHost = refUrl.hostname.replace(/^www\./, "");
    const siteHost = url.hostname.replace(/^www\./, "");

    // Same-site referrer → treat as direct (internal navigation)
    if (
      refHost === siteHost ||
      refHost.endsWith("." + siteHost) ||
      siteHost.endsWith("." + refHost)
    ) {
      return { source: "direct", medium: "none", landingPage, timestamp: now };
    }

    // Search engines
    for (const engine of SEARCH_ENGINES) {
      if (refHost.includes(engine.keyword)) {
        return {
          source: engine.name,
          medium: "organic",
          referringDomain: refHost,
          referringUrl: referrer,
          landingPage,
          searchQuery: refUrl.searchParams.get(engine.queryParam) || undefined,
          timestamp: now,
        };
      }
    }

    // Social networks (exact domain match with subdomain support)
    for (const [domain, name] of SOCIAL_DOMAINS) {
      if (domainEndsWith(refHost, domain)) {
        return {
          source: name,
          medium: "social",
          referringDomain: refHost,
          referringUrl: referrer,
          landingPage,
          timestamp: now,
        };
      }
    }

    // Generic referral
    return {
      source: refHost,
      medium: "referral",
      referringDomain: refHost,
      referringUrl: referrer,
      landingPage,
      timestamp: now,
    };
  }

  // ----- 4. Direct traffic (no referrer, no UTM) -----
  return { source: "direct", medium: "none", landingPage, timestamp: now };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * One-line human-readable label for emails and admin UIs.
 * Examples:
 *   "Google (organic search)"
 *   "Facebook (paid ad) — campaign: spring_sale"
 *   "techblog.com (referral)"
 *   "Direct traffic"
 */
export function formatTrafficSourceLabel(ts: TrafficSource): string {
  if (ts.source === "direct" && ts.medium === "none") {
    return "Direct traffic";
  }

  const name = ts.source.charAt(0).toUpperCase() + ts.source.slice(1);

  const MEDIUM_LABELS: Record<string, string> = {
    organic: "organic search",
    cpc: "paid ad",
    social: "social",
    email: "email",
    referral: "referral",
    none: "direct",
  };
  const mediumLabel = MEDIUM_LABELS[ts.medium] || ts.medium;

  let label = `${name} (${mediumLabel})`;
  if (ts.campaign) label += ` — campaign: ${ts.campaign}`;
  return label;
}

// ---------------------------------------------------------------------------
// Cookie reading (server-side)
// ---------------------------------------------------------------------------

/**
 * Read and parse the traffic source cookie using Next.js cookies().
 * Safe to call from any server component / route handler / server action.
 */
export async function readTrafficSourceCookie(): Promise<TrafficSource | null> {
  try {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    const raw = cookieStore.get(TRAFFIC_SOURCE_COOKIE)?.value;
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw)) as TrafficSource;
  } catch (error) {
    console.error(
      "[traffic-source] Failed to read traffic source cookie:",
      error
    );
    reportServerError(error, {
      source: "lib.analytics.traffic-source",
      feature: "analytics",
      action: "readTrafficSourceCookie",
      handled: true,
      severity: "warning",
    });
    return null;
  }
}

/**
 * Parse the traffic source cookie from a raw Cookie header string.
 * Used in Better Auth hooks where Next.js cookies() may not be available.
 */
export function parseTrafficSourceFromCookieHeader(
  cookieHeader: string | null | undefined
): TrafficSource | null {
  if (!cookieHeader) return null;
  try {
    const parts = cookieHeader.split(";").map((c) => c.trim());
    const match = parts.find((c) => c.startsWith(TRAFFIC_SOURCE_COOKIE + "="));
    if (!match) return null;
    const value = match.substring(TRAFFIC_SOURCE_COOKIE.length + 1);
    return JSON.parse(decodeURIComponent(value)) as TrafficSource;
  } catch (error) {
    console.error(
      "[traffic-source] Failed to parse traffic source from cookie header:",
      error
    );
    reportServerError(error, {
      source: "lib.analytics.traffic-source",
      feature: "analytics",
      action: "parseTrafficSourceFromCookieHeader",
      handled: true,
      severity: "warning",
    });
    return null;
  }
}
